/**
 * `lx_ask_user` — the SPA half of the structured-ask contract (Phase A).
 *
 * Plan: `ai-docs/upload-intent-and-ingest-closing-loop-miniplan.md` §4/§4-bis.
 * Runtime half: `lexruntime/openclaw/extensions/lexiun-core/src/tools/lx-ask-user.ts`.
 *
 * ┌─ THE FROZEN RUNTIME↔SPA CONTRACT (change BOTH sides in ONE commit) ───────┐
 * │ • `ASK_USER_ANSWERS_MARKER` prefixes the answers message. The runtime     │
 * │   tool description teaches the model to recognise it; a one-sided change  │
 * │   degrades the answers into an ordinary user message SILENTLY — the model │
 * │   still SEES the text, it just no longer knows it answers its own ask.    │
 * │ • `toolInput.questions[]` field names are matched name-for-name to the    │
 * │   runtime's `AskUserQuestionOut`: id · prompt · choices[{value,label,     │
 * │   description?}] · multiSelect? · allowOther?.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Why the card posts a plain chat message rather than a dedicated RPC: the
 * transcript then reads naturally to a human scrolling back, and chat-history
 * capture needs no change (A-0.3).
 */

/** FROZEN — must equal the runtime's `ASK_USER_ANSWERS_MARKER`. */
export const ASK_USER_ANSWERS_MARKER = "[respostas]";

/**
 * 🚨 THE SAME STRUCTURAL LIMITS THE TOOL ENFORCES — because a REFUSED ask still
 * BROADCASTS, and without these the SPA renders a card the tool rejected.
 *
 * Measured, not theorised: `ide`'s wizard smoke saw a refused call (stringified
 * args, pre-tolerance) render a card that was then REPLACED when the model's
 * retry produced a new `toolCallId`. For the replacement to happen, the FIRST —
 * refused — call must have rendered. Core emits `session.tool {phase:"start"}`
 * with the raw arguments around the call, so `execute()` returning `isError`
 * cannot un-send it.
 *
 * Why rendering a refused ask is worse than cosmetic: the MODEL was told its call
 * failed, so it does not believe it asked anything. A lawyer answering that card
 * posts `[respostas]` for questions no ask exists for — and an over-cap prompt
 * renders at the wizard's large type, which is the exact overflow the cap exists
 * to prevent.
 *
 * ⇒ FAIL-CLOSED: an ask violating these limits renders NOWHERE, exactly as an
 * out-of-scope ask does. The model's own reply text still reaches the lawyer, and
 * its retry produces a conforming ask.
 *
 * ⚠ Duplicated numerically because the tool ships on the IMAGE axis and this SPA
 * on the vite/zip axis, so they can skew. The invariant that makes skew safe is
 * **SPA ≥ tool**: equal or looser here only renders what the tool already
 * accepted; STRICTER here would hide a legitimate ask. Keep them equal, and if
 * the tool's caps ever change, change these in the same window.
 */
export const ASK_USER_LIMITS = {
  maxQuestions: 4,
  maxChoices: 6,
  maxPromptChars: 120,
  maxExplanationChars: 160,
  maxLabelChars: 28,
  maxTitleChars: 48,
  /** ⚖ T-1 — per-question dot label. MUST stay >= the tool's cap: a stricter
   *  cap here would HIDE a legitimate ask, the mirror of the harness's <= rule. */
  maxTopicChars: 16,
} as const;

export interface AskUserChoice {
  value: string;
  label: string;
  description?: string;
  /**
   * ⚖ D-4 — at most ONE per question (the TOOL refuses two, so the wire is
   * already clean). Rendered as a badge by the wizard.
   *
   * ⚠ Render defensively anyway: never show two badges even if a future writer
   * bypasses the tool. A wrong-looking recommendation on a lawyer-facing
   * decision is worse than none.
   */
  recommended?: boolean;
}

export interface AskUserQuestion {
  id: string;
  prompt: string;
  /** ⚖ D-4 — one-line "why this matters", shown under the question.
   *  ⚠ This is what replaces the shipped card's `description` TOOLTIP, which was
   *  invisible on touch — the field existed on the wire and not in the product. */
  explanation?: string;
  /** ⚖ T-1 — 1-2 word label naming THIS question (<=16). Feeds the wizard's
   *  clickable dots (tooltip + aria-label); absent ⇒ numeric fallback.
   *  ⚠ NOT the card-level `title` — see the tool's own note on why it is
   *  called `topic`. */
  topic?: string;
  choices?: AskUserChoice[];
  multiSelect?: boolean;
  allowOther?: boolean;
}

export interface AskUserCardData {
  /**
   * The session the ask BELONGS TO (the broadcast's top-level `sessionKey`,
   * sibling of `data`). Absent when the wire omits it.
   *
   * 🚨 Why the card must check it, measured live: the SPA's gateway connection
   * is per-USER, not per-case, and no sibling `use*FromAgent` hook filters on
   * scope. Harmless for "open this file"; NOT harmless for an ask. Observed:
   * a question asked in one case's conversation rendered in a DIFFERENT case's
   * composer after navigating. Answering there would have posted the answer
   * into the wrong case's thread — the asking agent never receives it, and the
   * other case's agent receives answers to a question it never asked.
   */
  sessionKey?: string;
  /**
   * Identity of THIS ask — the broadcast's `toolCallId`, which core mints per
   * tool call. Absent when the wire omits it (the hook then supplies a local
   * sequence, see `useAskUserFromAgent`).
   *
   * 🚨 Why an ask needs an identity at all, measured live: the card is
   * remounted per ask so a new ask cannot inherit the previous answers. Keying
   * on the QUESTION IDS is not enough — two consecutive asks that reuse the
   * same id produce the same key, React keeps the component's state, and the
   * earlier answer renders pre-selected under the NEW question text with the
   * counter claiming it was answered. Observed in the browser before this
   * field existed.
   */
  askId?: string;
  /** Optional short heading above the card. */
  title?: string;
  questions: AskUserQuestion[];
}

/**
 * Parse a `session.tool` payload into card data.
 *
 * Returns `null` for anything that is not a well-formed `lx_ask_user` start
 * event — a hook that renders on malformed input would put a broken card in
 * the lawyer's thread, and there is no user action that fixes that. Silence is
 * the correct degradation here: the model's own text still arrives.
 *
 * ⚠ Accepts BOTH `toolName`/`toolInput` and the legacy `name`/`args` spellings
 * — some core paths emit the latter (the shipped `use*CommandsFromAgent` hooks
 * carry the same tolerance).
 */
export function parseAskUserEvent(payload: unknown): AskUserCardData | null {
  const envelope = payload as
    | { data?: Record<string, unknown>; sessionKey?: unknown }
    | undefined;
  const data = envelope?.data;
  if (!data) return null;
  // Act on `start` only — the runtime echoes an `end` phase and rendering both
  // would double-fire the card.
  if (data.phase !== "start") return null;
  const toolName = (data.toolName ?? data.name) as string | undefined;
  if (toolName !== "lx_ask_user") return null;

  const input = (data.toolInput ?? data.args) as Record<string, unknown> | undefined;
  const rawQuestions = input?.questions;
  if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) return null;
  // Over the question cap ⇒ the tool refused this ask; do not render it.
  if (rawQuestions.length > ASK_USER_LIMITS.maxQuestions) return null;

  const questions: AskUserQuestion[] = [];
  for (const raw of rawQuestions) {
    if (typeof raw !== "object" || raw === null) return null;
    const rec = raw as Record<string, unknown>;
    const id = typeof rec.id === "string" ? rec.id.trim() : "";
    const prompt = typeof rec.prompt === "string" ? rec.prompt.trim() : "";
    if (!id || !prompt) return null;
    // Over-cap copy ⇒ refused by the tool. Rendering it would overflow the
    // wizard with text the model does not believe it sent.
    if (prompt.length > ASK_USER_LIMITS.maxPromptChars) return null;

    const question: AskUserQuestion = { id, prompt };
    const explanation = typeof rec.explanation === "string" ? rec.explanation.trim() : "";
    if (explanation.length > ASK_USER_LIMITS.maxExplanationChars) return null;
    if (explanation) question.explanation = explanation;
    // ⚖ T-1. This copy-in IS the feature. `question` is built as a CLOSED object
    // (see above), so a `topic` the tool emits reaches the wizard ONLY because
    // this line exists — without it the field would be emitted, carried over the
    // wire, and silently dropped here, with the dots showing numeric labels
    // forever and nothing erroring anywhere.
    //
    // ⚠ AND THIS FIELD SALVAGES — it does NOT fail closed like `prompt` and
    // `explanation` above. Corrected 2026-08-18 after `ide` measured the live
    // consequence; the first cut applied fail-closed uniformly and was wrong.
    //
    // Fail-closed is right when the TOOL WOULD HAVE REFUSED: the model knows the
    // call failed, so rendering a card it believes it never sent is the D-19
    // defect. It is wrong when the tool ACCEPTED, because then the model believes
    // it asked and a suppressed ask means the lawyer is asked NOTHING, silently —
    // the exact silent-no-op the fail-closed rule exists to prevent, produced by
    // the rule itself.
    //
    // Two things put `topic` on the accepting side:
    //   1. It is COSMETIC. An over-long `prompt` overflows the wizard's large
    //      type, so refusing to render it is a real protection; an over-long dot
    //      tooltip is not, and a numeric fallback already exists. Same reasoning
    //      as D-4's posture split — never kill the whole artifact over a badge.
    //   2. PRE-ROLL the deployed tool does not know the field, so it accepts ANY
    //      length. Failing closed here would make the SPA STRICTER than the
    //      shipped tool — which is the mirror rule backwards (SPA >= tool), and
    //      the rule must hold against the DEPLOYED tool, not the source one: the
    //      SPA is dev-served while the tool is baked into the image, so the two
    //      are never live at the same instant.
    const topic = typeof rec.topic === "string" ? rec.topic.trim() : "";
    if (topic && topic.length <= ASK_USER_LIMITS.maxTopicChars) question.topic = topic;
    if (Array.isArray(rec.choices)) {
      if (rec.choices.length > ASK_USER_LIMITS.maxChoices) return null;
      const choices: AskUserChoice[] = [];
      for (const c of rec.choices) {
        if (typeof c !== "object" || c === null) continue;
        const cr = c as Record<string, unknown>;
        const value = typeof cr.value === "string" ? cr.value : "";
        const label = typeof cr.label === "string" ? cr.label : "";
        if (!value || !label) continue;
        if (label.length > ASK_USER_LIMITS.maxLabelChars) return null;
        const description = typeof cr.description === "string" ? cr.description : undefined;
        const recommended = cr.recommended === true;
        choices.push({
          value,
          label,
          ...(description ? { description } : {}),
          ...(recommended ? { recommended: true } : {}),
        });
      }
      // Two recommendations ⇒ the tool refused the ask. Do NOT silently render
      // one badge: that would show the lawyer a recommendation the model never
      // successfully made.
      if (choices.filter((c) => c.recommended).length > 1) return null;
      if (choices.length > 0) question.choices = choices;
    }
    if (typeof rec.multiSelect === "boolean") question.multiSelect = rec.multiSelect;
    if (typeof rec.allowOther === "boolean") question.allowOther = rec.allowOther;
    questions.push(question);
  }

  const rawTitle = typeof input?.title === "string" ? input.title.trim() : "";
  if (rawTitle.length > ASK_USER_LIMITS.maxTitleChars) return null;
  const title = rawTitle || undefined;
  // `toolCallId` sits on the event envelope (beside `phase`/`name`), not inside
  // the tool's args — core mints it per tool call, which is exactly one ask.
  const askId = typeof data.toolCallId === "string" && data.toolCallId.trim()
    ? data.toolCallId.trim()
    : undefined;
  const sessionKey =
    typeof envelope?.sessionKey === "string" && envelope.sessionKey.trim()
      ? envelope.sessionKey.trim()
      : undefined;
  return {
    ...(sessionKey ? { sessionKey } : {}),
    ...(askId ? { askId } : {}),
    ...(title ? { title } : {}),
    questions,
  };
}

/**
 * Should THIS composer render THIS ask?
 *
 * 🚨 FAIL-CLOSED, and the asymmetry is the reason. Rendering an ask in the
 * wrong scope is not a cosmetic bug: the card posts its answers through the
 * CURRENT composer, so the answer lands in a conversation that never asked,
 * while the agent that did ask waits for something it will never receive.
 * A card that fails to render is a visible no-op the model's own reply text
 * still covers.
 *
 * So an ask with NO `sessionKey` is not rendered anywhere. That is a deliberate
 * refusal, not a silent drop — "we cannot tell which conversation this belongs
 * to" is exactly the state that must not be guessed. Core stamps `sessionKey`
 * on the tool payload whenever it can resolve one, so in practice the field is
 * present; its absence means the event is genuinely unattributable.
 */
export function askBelongsToSession(
  card: Pick<AskUserCardData, "sessionKey">,
  currentSessionKey: string | undefined,
): boolean {
  if (!card.sessionKey || !currentSessionKey) return false;
  return card.sessionKey === currentSessionKey;
}

/**
 * Stamp an ask identity, falling back to a local sequence when the wire omits
 * `toolCallId`.
 *
 * Extracted as a pure function (rather than living inside the hook) to match
 * this codebase's convention — `useNavCommandsFromAgent` extracts
 * `handleNavCommand` for exactly the same reason: the React wrapper only wires
 * deps and subscribes, so the logic worth pinning is testable without a
 * renderer.
 *
 * 🚨 The invariant it guarantees: **consecutive asks always differ**, even when
 * their questions are byte-identical. The card is remounted on `askId`
 * (`key=` in ChatInput), and without that a second ask inherits the first's
 * answers — measured in the browser, where a re-used question id showed the
 * previous chip pre-selected under new question text.
 */
export function withAskIdentity(card: AskUserCardData, localSeq: number): AskUserCardData {
  return card.askId ? card : { ...card, askId: `local-${localSeq}` };
}

/** One answered question, as collected by the card. */
export interface AskUserAnswer {
  id: string;
  prompt: string;
  /** Chosen labels and/or the free-text value, already human-readable. */
  values: string[];
}

/**
 * Render the answers as the message text posted back to the concierge.
 *
 * Shape (A-0.3) — structured enough for the model to key by id, readable
 * enough that a human scrolling the transcript sees a normal reply:
 *
 *   [respostas]
 *   • Quanto confiar nesta fonte? → Alta
 *   • Qual o papel? → Prova, Contexto
 *
 * Unanswered questions are OMITTED rather than sent as empty: an explicit
 * blank would read to the model as "the lawyer answered nothing", which is a
 * different fact from "the lawyer answered two of three". Absence keeps the
 * unknown unknown (`unknown-must-be-representable`).
 */
export function formatAnswersMessage(answers: AskUserAnswer[]): string {
  const lines = answers
    .filter((a) => a.values.length > 0)
    .map((a) => `• ${a.prompt} → ${a.values.join(", ")}`);
  return [ASK_USER_ANSWERS_MARKER, ...lines].join("\n");
}
